import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/apiClient";
import {
  PageHead, Card, Pill, StatusPill, Btn, FilterBar, Select, TextInput, Loading, Err, tableStyles, EmptyState, Drawer,
} from "./_shared";

const STATUSES = ["queued", "claimed", "running", "succeeded", "failed", "canceled", "retrying"];

export default function AdminJobs() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    const url = new URLSearchParams();
    if (status) url.set("status", status);
    if (q) url.set("q", q);
    const qs = url.toString();
    api.get("/api/admin/jobs" + (qs ? "?" + qs : "")).then((d) => setItems(d.items)).catch((e) => setErr(e.message));
  }, [status, q]);

  useEffect(() => { reload(); }, [reload]);

  const openJob = async (id) => {
    try {
      const res = await api.get(`/api/admin/jobs/${id}`);
      setActive(res);
    } catch (e) { setErr(e.message); }
  };

  const refund = async (id) => {
    setBusy(true);
    try { await api.post(`/api/admin/jobs/${id}/refund`); await reload(); setActive(null); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const retry = async (id) => {
    setBusy(true);
    try { await api.post(`/api/admin/jobs/${id}/retry`); await reload(); setActive(null); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (err) return <Err msg={err} />;

  return (
    <div data-testid="admin-jobs">
      <PageHead title="AI Jobs" sub={items ? `${items.length} jobs · last 300` : "Loading…"} />
      <FilterBar>
        <TextInput value={q} onChange={setQ} placeholder="Search prompt / job ID / workspace…" data-testid="jobs-search" />
        <Select value={status} onChange={setStatus} placeholder="All statuses" options={STATUSES} data-testid="jobs-filter" />
      </FilterBar>

      {!items ? <Loading /> : items.length === 0 ? (
        <EmptyState title="No jobs match these filters" />
      ) : (
        <Card pad={0} style={{ overflow: "hidden" }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Created</th>
                <th style={tableStyles.th}>Type</th>
                <th style={tableStyles.th}>Provider · model</th>
                <th style={tableStyles.th}>Status</th>
                <th style={tableStyles.th}>Prompt</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Credits</th>
                <th style={tableStyles.th}>Result</th>
              </tr>
            </thead>
            <tbody>
              {items.map((j) => (
                <tr key={j.id} data-testid={`job-row-${j.id}`} style={{ borderTop: "1px solid var(--pl-border)", cursor: "pointer" }}
                    onClick={() => openJob(j.id)}>
                  <td style={{ ...tableStyles.td, fontSize: 11 }}>{(j.created_at || "").slice(0, 19)}</td>
                  <td style={tableStyles.td}><Pill>{j.job_type}</Pill></td>
                  <td style={{ ...tableStyles.td, fontSize: 11 }}>{j.provider} / {j.model}</td>
                  <td style={tableStyles.td}><StatusPill s={j.status} /></td>
                  <td style={{ ...tableStyles.td, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }} title={j.prompt}>{j.prompt}</td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>
                    {j.credits_charged ?? 0} / {j.credits_estimated ?? 0}
                  </td>
                  <td style={tableStyles.td}>
                    {j.output_asset_url ? <a href={j.output_asset_url} target="_blank" rel="noreferrer" style={{ color: "var(--pl-aqua)" }}>view</a>
                      : j.error_message ? <span style={{ color: "#fda4af", fontSize: 11 }}>{j.error_code || "error"}</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Drawer open={!!active} onClose={() => setActive(null)} title={active ? `Job · ${active.job?.id?.slice(0, 12)}` : ""} width={620}>
        {active && (
          <div style={{ display: "grid", gap: 14 }}>
            <Card title="Summary">
              <Row k="Status" v={<StatusPill s={active.job.status} />} />
              <Row k="Type" v={active.job.job_type} />
              <Row k="Provider" v={`${active.job.provider} / ${active.job.model}`} />
              <Row k="Workspace" v={active.job.workspace_id} mono />
              <Row k="Created" v={(active.job.created_at || "").slice(0, 19)} />
              <Row k="Completed" v={(active.job.completed_at || "—").slice(0, 19)} />
              <Row k="Estimated / Charged" v={`${active.job.credits_estimated ?? 0} / ${active.job.credits_charged ?? 0}`} />
              {active.job.error_message && (
                <Row k="Error" v={<span style={{ color: "#fda4af" }}>{active.job.error_code}: {active.job.error_message}</span>} />
              )}
            </Card>
            <Card title={`Steps · ${active.steps?.length ?? 0}`}>
              {(!active.steps || active.steps.length === 0) ? (
                <div style={{ color: "var(--pl-text-muted)", fontSize: 12 }}>No steps recorded.</div>
              ) : (
                <table style={tableStyles.table}>
                  <thead><tr>
                    <th style={tableStyles.th}>When</th>
                    <th style={tableStyles.th}>Step</th>
                    <th style={tableStyles.th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {active.steps.map((s) => (
                      <tr key={s.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                        <td style={{ ...tableStyles.td, fontSize: 11 }}>{(s.created_at || "").slice(0, 19)}</td>
                        <td style={tableStyles.td}>{s.name || s.step}</td>
                        <td style={tableStyles.td}><StatusPill s={s.status || "—"} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn testId="job-retry" tone="primary" disabled={busy || !["failed", "canceled"].includes(active.job.status)}
                onClick={() => retry(active.job.id)}>Retry</Btn>
              <Btn testId="job-refund" tone="ghost" disabled={busy || active.job.status !== "failed" || !(active.job.credits_reserved > 0)}
                onClick={() => refund(active.job.id)}>Refund credits</Btn>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "7px 0", borderBottom: "1px dashed var(--pl-border)", fontSize: 12.5 }}>
      <span style={{ color: "var(--pl-text-muted)", fontWeight: 700 }}>{k}</span>
      <span style={{ fontFamily: mono ? "JetBrains Mono, monospace" : undefined, color: "var(--pl-text)" }}>{v ?? "—"}</span>
    </div>
  );
}
